// The worker API state shown in every ".api-state" element: pending until a core endpoint answers, live
// while /latest or /chart answer with a fresh indexed head, stale when they answer but the indexed head
// lags the chain, recorded when both core endpoints have failed. A small external store so any component
// can subscribe with useApiState().
import { useSyncExternalStore } from 'react';
import { fmtUtcS, snapDay, dur, errorText } from '../lib/format';
import { isOffError } from './client';
import { freshness, type FreshnessSource } from './freshness';

export type ApiMode = 'pending' | 'live' | 'stale' | 'recorded';

export interface ApiState {
  mode: ApiMode;
  /** Indexed head time of the last good core answer. */
  indexedAt: Date | null;
  headLagS: number | null;
  error: string | null;
  recordedFrom: string | null;
  recordedTo: string | null;
  /** An endpoint that answered before is failing now: its last answer stays on screen while the query retries. */
  delayed: boolean;
}

const CORE = ['chart', 'latest'] as const;
type Endpoint = 'chart' | 'latest' | 'session' | 'basis' | 'hook' | 'census';

let state: ApiState = { mode: 'pending', indexedAt: null, headLagS: null, error: null, recordedFrom: null, recordedTo: null, delayed: false };
const endpoint = new Map<Endpoint, boolean>();
const warned = new Set<Endpoint>();
const answered = new Set<Endpoint>();
const listeners = new Set<() => void>();

function set(next: Partial<ApiState>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new CustomEvent('bellguard:api', { detail: { mode: state.mode, indexedAt: state.indexedAt, error: state.error } }));
  }
}

export function apiText(s: ApiState = state): string {
  const t = modeText(s);
  return s.delayed && (s.mode === 'live' || s.mode === 'stale') ? `${t}; data delayed, retrying` : t;
}

function modeText(s: ApiState): string {
  if (s.mode === 'live') return `live, indexed ${s.indexedAt ? fmtUtcS(s.indexedAt) : 'n/a'}`;
  if (s.mode === 'stale') {
    const lag = s.headLagS !== null ? `, ${dur(s.headLagS * 1000)} behind the chain` : '';
    return `stale, indexed ${s.indexedAt ? fmtUtcS(s.indexedAt) : 'time n/a'}${lag}`;
  }
  if (s.mode === 'recorded') {
    const range = s.recordedFrom && s.recordedTo ? ` (data ${snapDay(s.recordedFrom)} to ${snapDay(s.recordedTo)} UTC)` : '';
    return `recorded, API unreachable${range}`;
  }
  return 'checking the live API';
}

/** A core endpoint answered; the payload's indexed head decides between live and stale. */
export function ok(name: Endpoint, payload?: FreshnessSource): void {
  endpoint.set(name, true);
  answered.add(name);
  const delayed = isDelayed();
  if (!CORE.includes(name as (typeof CORE)[number])) { set({ delayed }); return; }
  const f = freshness(payload ?? {});
  set({ mode: f.stale ? 'stale' : 'live', indexedAt: f.indexedAt, headLagS: f.headLagS, error: null, delayed });
}

/** An endpoint failed: warn once per endpoint; recorded only when both core endpoints have failed. */
export function fail(name: Endpoint, err: unknown): void {
  endpoint.set(name, false);
  if (!isOffError(err) && !warned.has(name)) {
    warned.add(name);
    console.warn(`bellguard api: ${name} unavailable (${errorText(err)}); ${CORE.includes(name as (typeof CORE)[number]) ? 'the page falls back to recorded data or direct reads' : 'the page uses its direct read'}`);
  }
  const delayed = isDelayed();
  // Once a core endpoint has answered, its data stays on screen: a failure is a delay, not a fall back.
  if (CORE.every((n) => endpoint.get(n) === false) && !CORE.some((n) => answered.has(n))) set({ mode: 'recorded', error: errorText(err) || 'API unreachable', delayed });
  else set({ delayed });
}

function isDelayed(): boolean {
  for (const n of answered) if (endpoint.get(n) === false) return true;
  return false;
}

export function setRecordedRange(from: string | null, to: string | null): void {
  set({ recordedFrom: from ?? state.recordedFrom, recordedTo: to ?? state.recordedTo });
}

export const getApiState = (): ApiState => state;

export function useApiState(): ApiState {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    getApiState,
    getApiState,
  );
}

/** Test hook: reset the store. */
export function resetApiState(): void {
  state = { mode: 'pending', indexedAt: null, headLagS: null, error: null, recordedFrom: null, recordedTo: null, delayed: false };
  endpoint.clear();
  warned.clear();
  answered.clear();
}
